import ApplicationAdapter from './application';
import { inject as service } from '@ember/service';
import DS, { ModelSchema } from 'ember-data';
import AccountService from 'cio/services/account';
import { ApiError, ApiResponse } from 'cio/services/api';
import { ModelRegistry } from 'ember-data/model';

const { InvalidError } = DS;

const HYDRA_WORKSPACE_REQUEST_REGEX = new RegExp(/\/hydra\/accounts\/\d+\/workspaces\/\d+/);

export default class EnvironmentAdapter extends ApplicationAdapter {
  @service account!: AccountService;

  /** TODO: Quick fix for CDP-only accounts without Journeys environments.
   * Hydra workspaces should probably have their own model, and this should
   * probably be handled at the route level. This is only used for the
   * workspace-settings page so far, only for renaming/changing the color
   * of workspaces, so for this use case it works, but should look into it */
  urlForFindRecord<K extends keyof ModelRegistry>(id: string, modelName: K, snapshot: DS.Snapshot<K>): string {
    if (modelName === 'environment' && !this.account.isJourneysAvailable) {
      return `/hydra/accounts/${this.account.current?.id}/workspaces/${id}`;
    }

    return super.urlForFindRecord(id, modelName, snapshot);
  }

  handleHydraWorkspacesResponse(
    status: number,
    headers: Record<string, any>,
    payload: ApiResponse,
    requestObject: Record<string, any>,
  ) {
    let response = {
      environment: {
        ...payload,
        hex_code: (payload as any)?.color,
      },
    };
    return super.handleResponse(status, headers, response, requestObject);
  }

  handleResponse(
    status: number,
    headers: Record<string, any>,
    payload: ApiResponse,
    requestObject: Record<string, any>,
  ) {
    let isHydraWorkspaceRequest = (requestObject.url as string).match(HYDRA_WORKSPACE_REQUEST_REGEX) != null;

    if (
      isHydraWorkspaceRequest &&
      String(requestObject.method).toLowerCase() === 'get' &&
      status === 200 &&
      !this.account.isJourneysAvailable
    ) {
      return this.handleHydraWorkspacesResponse(status, headers, payload, requestObject);
    }

    if (status !== 409 || !Array.isArray(payload.errors)) {
      return super.handleResponse(status, headers, payload, requestObject);
    }

    let error = payload.errors.find((e: ApiError) => /Workspace.*already exists/i.test(e.detail));
    if (!error) {
      return super.handleResponse(status, headers, payload, requestObject);
    }

    // turn 409 (conflict) response into generic invalid model error. 409 in
    // case of workspaces is caused by non-unique name - it can be turned into
    // "name" attribute error
    error.detail = 'A workspace with this name already exists';
    error.source = { pointer: '/data/attributes/name' };
    return new InvalidError(payload.errors);
  }

  buildURL(...args: Parameters<ApplicationAdapter['buildURL']>) {
    let url = super.buildURL(...args);

    // add account scope path param to "create Workspace" request path where
    // Workspace ID is not yet known:
    if (args[3] === 'createRecord') {
      return `${this.host}/${this.namespace}/accounts/${this.account.current.id}/environments`;
    }

    return url;
  }

  findRecord<K extends keyof ModelRegistry>(
    store: DS.Store,
    type: ModelSchema<K>,
    id: string,
    snapshot: DS.Snapshot<K>,
  ) {
    if (snapshot.adapterOptions?.isBackgroundTask === true) {
      let url = this.buildURL(type.modelName, id, snapshot, 'findRecord');
      let query = this.buildQuery(snapshot);
      return this.ajax(url, 'GET', {
        data: query,
        headers: {
          // Used by our metrics to determine whether a request is blocking or not
          'X-Is-Background-Request': 'true',
        },
      });
    }

    return super.findRecord(store, type, id, snapshot);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your adapters.
declare module 'ember-data/types/registries/adapter' {
  export default interface AdapterRegistry {
    environment: EnvironmentAdapter;
  }
}
